import React, { Fragment, FunctionComponent } from 'react';
import { Accordion, AccordionDetails, Typography, Grid, AccordionSummary, TextField } from '@material-ui/core';
import styles from './TcmReferralRequest.Styles';
import { EmptyDataLabel } from '../../../../generic-components/empty-data-label/EmptyDataLabel.Component';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import TcmCoverLetterEntity from '../../../../../entities/tcm/TcmCoverLetter.Entity';
import TcmTelephonicCaseManagementEntity from '../../../../../entities/tcm/TcmTelephonicCaseManagement.Entity';
import TcmMedicalInfoEntity from '../../../../../entities/tcm/TcmMedicalInfo.Entity';
import TcmTreatingProviderEntity from '../../../../../entities/tcm/TcmTreatingProvider.Entity';
import TcmOfficeVisitEntity from '../../../../../entities/tcm/TcmOfficeVisit.Entity';
import TcmWorkStatusEntity from '../../../../../entities/tcm/TcmWorkStatus.Entity';
import StateEntity from '../../../../../entities/State.Entity';
import Moment from 'moment';

interface ITcmReferralRequestProps {
    tcmCoverLetter?: TcmCoverLetterEntity;
    tcmTelephonicCaseManagement?: TcmTelephonicCaseManagementEntity;
    tcmMedicalInfo?: TcmMedicalInfoEntity;
    tcmTreatingProvider?: TcmTreatingProviderEntity;
    tcmOfficeVisit?: TcmOfficeVisitEntity;
    tcmWorkStatus?: TcmWorkStatusEntity;
    states: Array<StateEntity>;
}

const boolToYesNo = (value: boolean | string | undefined): string => {
    if (value === true || value === 'true') return 'Yes';
    if (value === false || value === 'false') return 'No';
    return '';
};

const renderCoverLetterContent = (tcmCoverLetter: TcmCoverLetterEntity, classes: any) => {
    if (!tcmCoverLetter) return <EmptyDataLabel />;
    
    return (
        <Grid container spacing={3} className={classes.gridContainer}>
            <Grid item xs={12}>
                <Typography variant="body2" className={classes.dataLabel}>Cover Letter Required</Typography>
                <Typography variant="subtitle2" className={classes.blackFont}>
                    {tcmCoverLetter.isCoverLetterRequired ? 'Yes' : 'No'}
                </Typography>
            </Grid>
            {tcmCoverLetter.isCoverLetterRequired && renderCoverLetterFields(tcmCoverLetter, classes)}
        </Grid>
    );
};

const renderCoverLetterFields = (tcmCoverLetter: TcmCoverLetterEntity, classes: any) => (
    <Fragment>
        <Grid item xs={12}>
            <TextField
                label="Cover Letter Will Be Provided Separately"
                maxRows={6}
                multiline
                className={classes.input}
                disabled
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                value={tcmCoverLetter.coverLetterWillBeProvidedSeparately}
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                label="Prepare Medical Summary"
                maxRows={6}
                multiline
                className={classes.input}
                disabled
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                value={tcmCoverLetter.prepareMedicalSummary}
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                label="Detailed History, Diagnosis and Prognosis"
                maxRows={6}
                multiline
                className={classes.input}
                disabled
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                value={tcmCoverLetter.detailedHistoryDiagnosisPrognosis}
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                label="Casual Relationship"
                maxRows={6}
                multiline
                className={classes.input}
                disabled
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                value={tcmCoverLetter.casualRelationship}
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                label="Treatment Reasonable and Necessary"
                maxRows={6}
                multiline
                className={classes.input}
                disabled
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                value={tcmCoverLetter.treatmentReasonableNecessary}
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                label="Work Capabilities"
                maxRows={6}
                multiline
                className={classes.input}
                disabled
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                value={tcmCoverLetter.workCapabilities}
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                label="Reached Maximum Medical Improvement (MMI)"
                maxRows={6}
                multiline
                className={classes.input}
                disabled
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                value={tcmCoverLetter.reachedMMI}
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                label="Permanent Impairment Rating"
                maxRows={6}
                multiline
                className={classes.input}
                disabled
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                value={tcmCoverLetter.permanentImpairmentRating}
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                label="Further Treatment Recommendations"
                maxRows={6}
                multiline
                className={classes.input}
                disabled
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                value={tcmCoverLetter.furtherTreatmentRecommendations}
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                label="Additional Questions"
                maxRows={6}
                multiline
                className={classes.input}
                disabled
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                value={tcmCoverLetter.additionalQuestions}
            />
        </Grid>
    </Fragment>
);

const renderTelephonicCaseManagement = (tcmTelephonic: TcmTelephonicCaseManagementEntity, classes: any) => {
    if (!tcmTelephonic) return <EmptyDataLabel />;
    
    return (
        <Grid container spacing={3} className={classes.gridContainer}>
            <Grid item xs={3}>
                <Typography variant="body2" className={classes.dataLabel}>Rush Request</Typography>
                <Typography variant="subtitle2" className={classes.blackFont}>
                    {tcmTelephonic.rushRequest ? 'Yes' : 'No'}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Referral Instructions"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={tcmTelephonic.referralInstructions || ''}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Comments"
                    maxRows={6}
                    multiline
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={tcmTelephonic.comments || ''}
                />
            </Grid>
        </Grid>
    );
};

const renderMedicalInfo = (
    tcmMedicalInfo: TcmMedicalInfoEntity, 
    classes: any
) => {
    if (!tcmMedicalInfo) return <EmptyDataLabel />;
    
    return (
        <Grid container spacing={3} className={classes.gridContainer}>
            <Grid item xs={6}>
                <TextField
                    label="Surgery Date"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={tcmMedicalInfo?.surgeryDate
                        ? Moment(tcmMedicalInfo.surgeryDate).format('MM/DD/YYYY')
                        : ''
                    }
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    label="Primary Diagnosis"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={tcmMedicalInfo.primaryDiagnosis || ''}
                />
            </Grid>
        </Grid>
    );
};

const MakeFullAddress = (treatingProviderState: TcmTreatingProviderEntity, states: Array<StateEntity>) => {
  // Build the address string while keeping the original spacing/format as close as possible
  const stateName = states?.find(element => element.stateId==treatingProviderState.stateId)?.stateName;
  const streetSegment = treatingProviderState.addressLine1 ? `${treatingProviderState.addressLine1}, ` : '';
  const addressLine2Segment = treatingProviderState.addressLine2 ? `${treatingProviderState.addressLine2}, ` : '';
  const citySegment = treatingProviderState.cityName ? `${treatingProviderState.cityName}, ` : '';
  const stateSegment = stateName ? `${stateName} ` : '';
  const zipCodeSegment = treatingProviderState.zipCode ? `${treatingProviderState.zipCode}` : '';

  return `${streetSegment}${addressLine2Segment}${citySegment}${stateSegment}${zipCodeSegment}`;
};
const renderTreatingProvider = (tcmTreatingProvider: TcmTreatingProviderEntity,  states: Array<StateEntity>, classes: any) => {
    if (!tcmTreatingProvider) return <EmptyDataLabel />;
    
    return (
        <Grid container spacing={3} className={classes.gridContainer}>
            <Grid item xs={6}>
                <TextField
                    label="Fisrt Name"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={`${tcmTreatingProvider.firstName || ''}`}
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    label="Address"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={MakeFullAddress(tcmTreatingProvider, states)}
                />                
            </Grid>
            <Grid item xs={6}>
                 <TextField
                    label="Middle Initial"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={`${tcmTreatingProvider.middleInitial || ''}`}
                />
            </Grid>
            <Grid item xs={6}>
 <TextField
                    label="Telephone Number"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={`${tcmTreatingProvider.telephoneNumber || ''}`}
                />
                </Grid>
            <Grid item xs={6}>
                <TextField
                    label="Last Name"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={`${tcmTreatingProvider.lastName || ''}`}
                />
            </Grid>
            <Grid item xs={6}> 
                <TextField
                    label="In Network"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={boolToYesNo(tcmTreatingProvider.inNetwork)}
                />
            </Grid>

         </Grid>
    );
};

const renderOfficeVisit = (tcmOfficeVisit: TcmOfficeVisitEntity, classes: any) => {
    if (!tcmOfficeVisit) return <EmptyDataLabel />;
    
    return (
        <Grid container spacing={3} className={classes.gridContainer}>
            <Grid item xs={6}>
                <TextField
                    label="Last Office Visit Date"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={tcmOfficeVisit.lastOfficeVisitDate
                        ? Moment(tcmOfficeVisit.lastOfficeVisitDate).format('MM/DD/YYYY')
                        : ''
                    }
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    label="Next Office Visit Date"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={tcmOfficeVisit.nextOfficeVisitDate
                        ? Moment(tcmOfficeVisit.nextOfficeVisitDate).format('MM/DD/YYYY')
                        : ''
                    }
                />
            </Grid>
        </Grid>
    );
};

const renderWorkStatus = (tcmWorkStatus: TcmWorkStatusEntity, classes: any) => {
    if (!tcmWorkStatus) return <EmptyDataLabel />;
    
    return (
        <Grid container spacing={3} className={classes.gridContainer}>
            <Grid item xs={6}>
                <TextField
                    label="Physical Demand Level"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={tcmWorkStatus.physicalDemandLevel || ''}
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    label="Work Status"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={tcmWorkStatus.workStatus || ''}
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    label="Work Status Effective Date"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={tcmWorkStatus.workStatusEffectiveDate
                        ? Moment(tcmWorkStatus.workStatusEffectiveDate).format('MM/DD/YYYY')
                        : ''
                    }
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    label="Estimated RTW Date"
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={tcmWorkStatus.estimatedRtwDate
                        ? Moment(tcmWorkStatus.estimatedRtwDate).format('MM/DD/YYYY')
                        : ''
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Work Status Remark"
                    maxRows={6}
                    multiline
                    className={classes.input}
                    disabled
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    value={tcmWorkStatus.workStatusRemark || ''}
                />
            </Grid>
        </Grid>
    );
};

export const TcmReferralRequest: FunctionComponent<ITcmReferralRequestProps> = (props: ITcmReferralRequestProps) => {
    const classes = styles();

    return (
        <Fragment>        
        {/* Telephonic Case Management */}
        <Accordion defaultExpanded={true} className={classes.paper} square>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                aria-controls="panel2a-content"
                id="tcm-telephonic-accordion"
                className={classes.paperHeader}
            >
                <Typography variant="subtitle2" className={classes.paperTitle}>Telephonic Case Management</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {renderTelephonicCaseManagement(props.tcmTelephonicCaseManagement, classes)}
            </AccordionDetails>
        </Accordion>

        {/* Medical & Diagnosis Information */}
        <Accordion defaultExpanded={true} className={classes.paper} square>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                aria-controls="panel3a-content"
                id="tcm-medical-info-accordion"
                className={classes.paperHeader}
            >
                <Typography variant="subtitle2" className={classes.paperTitle}>Medical & Diagnosis Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {renderMedicalInfo(props.tcmMedicalInfo, classes)}
            </AccordionDetails>
        </Accordion>

        {/* Treating Provider Information */}
        <Accordion defaultExpanded={true} className={classes.paper} square>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                aria-controls="panel4a-content"
                id="tcm-treating-provider-accordion"
                className={classes.paperHeader}
            >
                <Typography variant="subtitle2" className={classes.paperTitle}>Treating Provider Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {renderTreatingProvider(props.tcmTreatingProvider, props.states, classes)}
            </AccordionDetails>
        </Accordion>

        {/* Office Visit Information */}
        <Accordion defaultExpanded={true} className={classes.paper} square>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                aria-controls="panel5a-content"
                id="tcm-office-visit-accordion"
                className={classes.paperHeader}
            >
                <Typography variant="subtitle2" className={classes.paperTitle}>Office Visit Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {renderOfficeVisit(props.tcmOfficeVisit, classes)}
            </AccordionDetails>
        </Accordion>

        {/* Work Status Information */}
        <Accordion defaultExpanded={true} className={classes.paper} square>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                aria-controls="panel6a-content"
                id="tcm-work-status-accordion"
                className={classes.paperHeader}
            >
                <Typography variant="subtitle2" className={classes.paperTitle}>Work Status Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {renderWorkStatus(props.tcmWorkStatus, classes)}
            </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded={true} className={classes.paper} square>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                aria-controls="panel1a-content"
                id="cms-cover-letter-accordion"
                className={classes.paperHeader}
            >
                <Typography variant="subtitle2" className={classes.paperTitle}>Cover Letter</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {renderCoverLetterContent(props.tcmCoverLetter, classes)}
            </AccordionDetails>
        </Accordion>
    </Fragment>
    );
}

